import { PageHeader, Panel, Banner } from '../../ds/index'
import { useApi } from '../../lib/useApi'
import { money, dateShort } from '../../lib/format'
import { LoadingBlock, ErrorBlock } from '../../components/Loading'

type Cell = { lane_id: string; lane: string; rank: number; tier_key: string; tier: string; tier_rank: number; full_cents: number; tab_cents: number }
type Pricing = {
  category: string | null; trade_job_tier: string | null; tab_discount_bps: number; price_notice_days: number
  grid: Cell[]; job_types: { name: string; lane: string }[]
  upcoming: { id: string; description: string; effective_at: string }[]
}

/**
 * The business's prices (Business Terms 4.1 and Exhibit B): every conversation
 * tier at every job tier, full price and tab price, which job tier its trade and
 * job types sit in, and any change already announced (4.3).
 */
export default function ClientPricing() {
  const { data, error } = useApi<Pricing>('/api/client/pricing')
  if (error) return <ErrorBlock error={error} />
  if (!data) return <LoadingBlock />
  const lanes = [...new Map(data.grid.map((c) => [c.lane_id, c])).values()].sort((a, b) => a.rank - b.rank)
  const tiers = [...new Map(data.grid.map((c) => [c.tier_key, c])).values()].sort((a, b) => b.tier_rank - a.tier_rank)
  const cell = (lane: string, tier: string) => data.grid.find((c) => c.lane_id === lane && c.tier_key === tier)
  const off = data.tab_discount_bps / 100
  return (
    <>
      <PageHeader title="Your prices" subtitle={`What a lead costs${data.category ? ` in ${data.category}` : ''}: by how far the conversation went and by the size of the job.`} />
      {data.upcoming.length > 0 && (
        <div style={{ marginBottom: 'var(--s-6)' }}>
          <Banner tone="warn" title="A price change is coming">
            {data.upcoming.map((u) => <div key={u.id}>{u.description}, from {dateShort(u.effective_at)}.</div>)}
            <div style={{ marginTop: 6 }}>It never applies to a lead already sent to you.</div>
          </Banner>
        </div>
      )}
      <Panel>
        <div className="panel-header">
          <h2 className="panel-title">Price per lead</h2>
          <span className="muted">Full price / tab price ({off}% off, paid from your tab)</span>
        </div>
        <div className="table-wrap">
          <table className="table">
            <thead><tr><th>Conversation</th>{lanes.map((l) => <th key={l.lane_id}>{l.lane}{data.trade_job_tier === l.lane ? <div className="muted">your trade</div> : null}</th>)}</tr></thead>
            <tbody>
              {tiers.map((t) => (
                <tr key={t.tier_key}>
                  <td><strong>{t.tier}</strong></td>
                  {lanes.map((l) => {
                    const c = cell(l.lane_id, t.tier_key)
                    return <td key={l.lane_id} className="num">{c ? <>{money(c.full_cents)} <span className="muted">/ {money(c.tab_cents)}</span></> : ''}</td>
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="muted" style={{ marginBottom: 0 }}>
          C: we recommended you and asked a question. B: they answered one. A: they gave three or more answers. A+: they are calling you, or we connected you.
        </p>
        <p className="muted" style={{ marginBottom: 0 }}>
          A lead's tier can keep rising until you claim it, which locks it (Business Terms 3.2). At 11:59 PM Sunday, Arizona time, every lead from the week is claimed at the tier it reached and invoiced: your tab credit pays first, at the tab price, and the rest is charged to your card at the full price.
        </p>
      </Panel>
      {data.job_types.length > 0 && (
        <Panel>
          <div className="panel-header"><h2 className="panel-title">Job sizes in your trade</h2></div>
          <div className="stack-tight">
            {data.job_types.map((j) => (
              <div key={j.name} className="row-tight" style={{ justifyContent: 'space-between' }}><span>{j.name}</span><span className="muted">{j.lane}</span></div>
            ))}
          </div>
          <p className="muted" style={{ marginBottom: 0 }}>A lead whose job does not fit one of these is priced at your trade's job size{data.trade_job_tier ? ` (${data.trade_job_tier})` : ''}.</p>
        </Panel>
      )}
      <p className="muted">Prices change only with at least {data.price_notice_days || 3} days' notice by email (Business Terms 4.3).</p>
    </>
  )
}
